import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { combineLatest, fromEvent, Observable, Subject } from 'rxjs';

import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { rxSetInterval } from '../../../../../../libs/utils/src';
import { AbstractLogger, LoggerFactory } from '../diagnostics';
import { UrlProvider } from '../url';
import { AppVersionService } from './abstract';
import { IAppInfo } from './model';

@Injectable()
export class AppVersionServiceImpl implements AppVersionService {
	constructor(
		logger: LoggerFactory,
		readonly updates: SwUpdate,
		readonly zone: NgZone,
		readonly url: UrlProvider,
		readonly http: HttpClient
	) {
		this._logger = logger.createLogger('core/version');
		this._init();
	}

	private _logger: AbstractLogger;
	private _appInfo: Partial<IAppInfo> = {};
	private _keyStorageBuildHash = 'appInfo:storageBuildHash';
	private _newVersionAvailable = false;
	private _checkingNewVersion = false;

	private _updateStarted$ = new Subject();
	readonly updateStarted$: Observable<any> = this._updateStarted$.asObservable();
	readonly pageVisible$ = fromEvent(document, 'visibilitychange', { capture: false }).pipe(
		startWith(!document.hidden),
		map(_ => !document.hidden),
		shareReplay({ refCount: true, bufferSize: 1 })
	);
	readonly defaultRuntimeBuildTime = Date.now();

	get appInfo(): Partial<IAppInfo> {
		return this._appInfo;
	}

	private _init() {
		let buildHash = localStorage.getItem(this._keyStorageBuildHash) || null;
		this.update({ buildHash });
		if (!this.updates.isEnabled) {
			return this._logger.info('Service worker is not enabled');
		}
		this.zone.runOutsideAngular(() => {
			combineLatest([rxSetInterval(2 * 60 * 1000), this.pageVisible$])
				.pipe(filter(([timer, isPageActive]) => isPageActive))
				.subscribe(() => {
					if (this._checkingNewVersion) {
						return;
					}
					if (!this._newVersionAvailable) {
						this._checkingNewVersion = true;
						this.updates
							.checkForUpdate()
							.then(() => {
								this._checkingNewVersion = false;
							})
							.catch(err => {
								this._logger.error(err.message || String(err), err);
								this._checkingNewVersion = false;
							});
					}
				});
		});
		this.updates.available.subscribe(event => {
			this._newVersionAvailable = true;
			this._logger.forceLogInfo(
				`Available new version client: ${event.available.hash}, current: ${event.current.hash}`,
				event
			);
			buildHash = event.available.hash;
			// пока обновляем не спрашивая
			if (true || confirm('Доступна новая версия приложения. Обновить?')) {
				this._updateStarted$.next(0 as any);
				this.updates.activateUpdate().then(() => {
					this._logger.info('Installing new version client...', this.appInfo);
					localStorage.setItem(this._keyStorageBuildHash, buildHash);
					this.update({ buildHash });
					this._newVersionAvailable = false;
					document.location.reload();
				});
			}
		});
		this.updates.activated.subscribe(event => {
			this._logger.forceLogInfo(`New version activated: ${this.appInfo.version}.${event.current.hash}`, {
				event,
				appInfo: this.appInfo,
			});
		});
	}

	update(info: Partial<IAppInfo>) {
		this._appInfo = Object.assign(this._appInfo, info);
	}

	/**
	 * return object parsed from assets/release-info/app-info.json file
	 * that file auto generate after deploy
	 */
	getAppInfoFromAssetFile() {
		// fixme: set url in config
		return this.http.get<Partial<IAppInfo>>(
			this.url.getUrlRelativeToAssets(`release-info/app-info.json?_=${Date.now()}`),
			{ headers: { 'ignore-license-busy': 'true' } }
		);
	}

	hasRuntimeBuildNumber() {
		const info = window['@valhalla/spa-release-info'];
		return info?.build !== undefined;
	}

	getRuntimeBuildNumber() {
		const info = window['@valhalla/spa-release-info'];
		return info?.build;
	}

	getRuntimeBuildTime() {
		const info = window['@valhalla/spa-release-info'];
		return Number(info?.time || this.defaultRuntimeBuildTime);
	}

	getRuntimeGitHash() {
		const info = window['@valhalla/spa-release-info'];
		return info?.gitHash || '';
	}
}
